import { useCallback, useState, type FormEvent } from "react";
import type { ApiError } from "../api/client";
import { useApi } from "../api/context";
import { CopyButton } from "../components/CopyButton";
import { Field } from "../components/Field";
import { StatusBadge } from "../components/StatusBadge";
import { toApiError } from "../lib/errors";
import { useDocumentTitle } from "../lib/useDocumentTitle";
import { useLoader } from "../lib/useLoader";
import { useWorkspace } from "./workspace";

function toOrigin(value: string): string | null {
  try {
    const url = new URL(value.trim());
    const local = url.hostname === "localhost" || url.hostname === "127.0.0.1";
    return url.protocol === "https:" || (url.protocol === "http:" && local) ? url.origin : null;
  } catch {
    return null;
  }
}

export function EmbedPage() {
  useDocumentTitle("Website embed · TryLook");
  const api = useApi();
  const { tenantId } = useWorkspace();
  const loadEmbed = useCallback(() => api.getEmbed(tenantId), [api, tenantId]);
  const embed = useLoader(loadEmbed);

  const [draft, setDraft] = useState<string[] | null>(null);
  const [newOrigin, setNewOrigin] = useState("");
  const [inputError, setInputError] = useState<string | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<ApiError | null>(null);
  const [saved, setSaved] = useState(false);

  const origins = draft ?? embed.data?.allowed_origins ?? [];

  function addOrigin(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const origin = toOrigin(newOrigin);
    if (!origin) {
      setInputError("Enter a full website address starting with https://, like https://www.example.com");
      return;
    }
    setInputError(undefined);
    setSaved(false);
    if (!origins.includes(origin)) {
      setDraft([...origins, origin]);
    }
    setNewOrigin("");
  }

  function removeOrigin(origin: string) {
    setSaved(false);
    setDraft(origins.filter((existing) => existing !== origin));
  }

  async function save() {
    setSaving(true);
    setSaveError(null);
    try {
      embed.replace(await api.updateOrigins(origins, tenantId));
      setDraft(null);
      setSaved(true);
    } catch (error) {
      setSaveError(toApiError(error));
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <h1>Website embed</h1>
        <p>
          Add a “Book a live demo” button to your website. Visitors talk to the AI guide without leaving your page.
        </p>
      </div>

      {embed.error && (
        <p className="alert alert-error" role="alert">
          {embed.error.message}
        </p>
      )}
      {!embed.data && !embed.error && (
        <p className="row muted" role="status">
          <span className="spinner" aria-hidden="true" /> Loading embed settings…
        </p>
      )}

      {embed.data && !embed.data.ready && (
        <section className="card stack">
          <div className="card-head">
            <h2 className="card-title">Your workspace is still being set up</h2>
            <StatusBadge status={embed.data.status} />
          </div>
          <p className="muted">
            The TryLook team is mapping your product and tuning the guide. As soon as it's ready to go live, your embed
            tag appears here. Meanwhile, add the websites where you'll use it.
          </p>
        </section>
      )}

      {embed.data?.ready && embed.data.snippet && (
        <section className="card stack" data-tour="embed-tag">
          <div className="card-head">
            <h2 className="card-title">Your embed tag</h2>
            <CopyButton value={embed.data.snippet} label="Copy tag" />
          </div>
          <pre className="code-block">
            <code>{embed.data.snippet}</code>
          </pre>
          <ol className="steps-list">
            <li>Paste it just before the closing &lt;/body&gt; tag on each page where the button should appear.</li>
            <li>Add your website's address to the allowed websites below. Demos won't start anywhere else.</li>
            <li>
              Optional: customize with <code>data-label</code>, <code>data-position="left"</code>, or{" "}
              <code>data-color="#0f766e"</code>.
            </li>
          </ol>
        </section>
      )}

      {embed.data && (
        <section className="card stack">
          <h2 className="card-title">Allowed websites</h2>
          {origins.length === 0 ? (
            <p className="empty">No websites yet.</p>
          ) : (
            <ul className="origin-list">
              {origins.map((origin) => (
                <li key={origin}>
                  {origin}
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => removeOrigin(origin)}
                    aria-label={`Remove ${origin}`}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
          <form onSubmit={addOrigin} noValidate>
            <Field label="Website address" error={inputError}>
              {(props) => (
                <div className="inline-form">
                  <input
                    {...props}
                    type="url"
                    inputMode="url"
                    placeholder="https://www.example.com"
                    value={newOrigin}
                    onChange={(event) => setNewOrigin(event.target.value)}
                  />
                  <button type="submit" className="btn btn-secondary">
                    Add
                  </button>
                </div>
              )}
            </Field>
          </form>
          {saveError && (
            <p className="alert alert-error" role="alert">
              {saveError.details[0]?.message ?? saveError.message}
            </p>
          )}
          {saved && (
            <p className="alert alert-success" role="status">
              Allowed websites saved.
            </p>
          )}
          <div className="row">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => void save()}
              disabled={draft === null || saving}
            >
              {saving ? "Saving…" : "Save websites"}
            </button>
          </div>
        </section>
      )}
    </>
  );
}
